/**
 * Cliente REST do ROBÔ Suporte/Comercial (raiz /rest/robosc/*), restrito ao perfil CLIENTEFIEL.
 *
 * Instância axios própria (isolada do interceptor de troca-de-loja do painel): o header
 * `estabelecimento` aqui é SEMPRE o estabelecimento do operador logado (o tenant que roda o robô),
 * não a loja-cliente que o painel por acaso tenha selecionado. `authorization` é o mesmo
 * `chaveSistema` do login do painel.
 */
import axios from "axios"
import { getRestApiBaseUrl } from "../utils/restApiBaseUrl"
import useAppStore from "../stores/AppStore"

const apiRobo = axios.create({ baseURL: getRestApiBaseUrl() })

function estabelecimentoOperador(): number | undefined {
    // Prefere a loja SELECIONADA no painel (troca de estabelecimento) — assim o operador
    // CLIENTEFIEL escolhe o estabelecimento DO ROBÔ (com o número oficial próprio do robô,
    // separado do número da Izza). Cai para o estabelecimento do próprio usuário se nenhuma
    // loja estiver selecionada.
    try {
        const estab = JSON.parse(localStorage.getItem("estabelecimentoAtualCF") || "null")
        if (estab?.id) return estab.id
    } catch (_) {}
    try {
        const cfg = useAppStore.getState().configuracoes
        const id = cfg?.user?.parametros?.estabelecimento?.id
        if (id) return id
    } catch (_) {}
    return undefined
}

apiRobo.interceptors.request.use(config => {
    try {
        config.headers = config.headers || {}
        const chave = localStorage.getItem("chaveSistema")
        if (chave) config.headers.authorization = chave
        const estab = estabelecimentoOperador()
        if (estab) config.headers.estabelecimento = estab
        config.headers.sistema = "painel"
    } catch (_) {}
    return config
})

export type Dict = Record<string, any>

// ─── Configuração ──────────────────────────────────────────────────────────────
export const RoboConfig = {
    status: () => apiRobo.get("robosc/status").then(r => r.data),
    ligarRobo: (ativo: boolean) => apiRobo.post("robosc/robo-ativo", { ativo }).then(r => r.data),
    obter: () => apiRobo.get("robosc/config").then(r => r.data),
    salvar: (config: Dict) => apiRobo.put("robosc/config", config).then(r => r.data),
    testar: (provedor: string) => apiRobo.post(`robosc/config/testar/${provedor}`).then(r => r.data),
    listarPrompts: () => apiRobo.get("robosc/prompts").then(r => r.data),
    salvarPrompt: (id: string, texto: string) =>
        apiRobo.put(`robosc/prompts/${id}`, { texto }).then(r => r.data),
    restaurarPrompt: (id: string) => apiRobo.delete(`robosc/prompts/${id}`).then(r => r.data),
    listarLogs: (params?: Dict) => apiRobo.get("robosc/logs", { params }).then(r => r.data),
    listarUsuarios: () => apiRobo.get("robosc/usuarios").then(r => r.data),
    salvarUsuario: (body: Dict) => apiRobo.post("robosc/usuarios", body).then(r => r.data),
    removerUsuario: (id: number) => apiRobo.delete(`robosc/usuarios/${id}`).then(r => r.data),
    listarMensagensRapidas: () => apiRobo.get("robosc/mensagens-rapidas").then(r => r.data),
    salvarMensagemRapida: (body: Dict) =>
        apiRobo.post("robosc/mensagens-rapidas", body).then(r => r.data),
    removerMensagemRapida: (id: number) =>
        apiRobo.delete(`robosc/mensagens-rapidas/${id}`).then(r => r.data),
}

// ─── Suporte ─────────────────────────────────────────────────────────────────
export const RoboSuporte = {
    listarChats: (params?: Dict) => apiRobo.get("robosc/suporte/chats", { params }).then(r => r.data),
    detalheChat: (id: number) => apiRobo.get(`robosc/suporte/chats/${id}`).then(r => r.data),
    contextoCliente: (id: number) =>
        apiRobo.get(`robosc/suporte/chats/${id}/contexto-cliente`).then(r => r.data),
    mensagens: (id: number, params?: Dict) =>
        apiRobo.get(`robosc/suporte/chats/${id}/mensagens`, { params }).then(r => r.data),
    enviarMensagem: (id: number, texto: string) =>
        apiRobo.post(`robosc/suporte/chats/${id}/enviar`, { texto }).then(r => r.data),
    pausarIa: (id: number, body?: Dict) =>
        apiRobo.post(`robosc/suporte/chats/${id}/ia/pausar`, body || {}).then(r => r.data),
    retomarIa: (id: number) =>
        apiRobo.post(`robosc/suporte/chats/${id}/ia/retomar`, {}).then(r => r.data),
    listarNotas: (id: number) => apiRobo.get(`robosc/suporte/chats/${id}/notas`).then(r => r.data),
    adicionarNota: (id: number, corpo: string) =>
        apiRobo.post(`robosc/suporte/chats/${id}/notas`, { corpo }).then(r => r.data),
    removerNota: (notaId: number) =>
        apiRobo.delete(`robosc/suporte/notas/${notaId}`).then(r => r.data),
    listarEstabelecimentos: (id: number) =>
        apiRobo.get(`robosc/suporte/chats/${id}/estabelecimentos`).then(r => r.data),
    vincularEstabelecimento: (id: number, body: Dict) =>
        apiRobo.post(`robosc/suporte/chats/${id}/estabelecimentos`, body).then(r => r.data),
    desvincularEstabelecimento: (id: number, vinculoId: number) =>
        apiRobo.delete(`robosc/suporte/chats/${id}/estabelecimentos/${vinculoId}`).then(r => r.data),
    listarTickets: (params?: Dict) =>
        apiRobo.get("robosc/suporte/tickets", { params }).then(r => r.data),
    pegarTicket: (id: number) =>
        apiRobo.post(`robosc/suporte/tickets/${id}/pegar`, {}).then(r => r.data),
    transferirTicket: (id: number, body: Dict) =>
        apiRobo.post(`robosc/suporte/tickets/${id}/transferir`, body).then(r => r.data),
    fecharTicket: (id: number, body?: Dict) =>
        apiRobo.post(`robosc/suporte/tickets/${id}/fechar`, body || {}).then(r => r.data),
    reabrirTicket: (id: number) =>
        apiRobo.post(`robosc/suporte/tickets/${id}/reabrir`, {}).then(r => r.data),
    estatisticas: () => apiRobo.get("robosc/suporte/estatisticas").then(r => r.data),
    criarGrupo: (body: Dict) => apiRobo.post("robosc/suporte/grupos", body).then(r => r.data),
}

// ─── Comercial ─────────────────────────────────────────────────────────────────
export const RoboComercial = {
    listarLeads: (params?: Dict) => apiRobo.get("robosc/comercial/leads", { params }).then(r => r.data),
    facetas: () => apiRobo.get("robosc/comercial/leads/facetas").then(r => r.data),
    kanban: (params?: Dict) =>
        apiRobo.get("robosc/comercial/leads/kanban", { params }).then(r => r.data),
    coluna: (params?: Dict) =>
        apiRobo.get("robosc/comercial/leads/coluna", { params }).then(r => r.data),
    salvarLead: (body: Dict) => apiRobo.post("robosc/comercial/leads", body).then(r => r.data),
    detalheLead: (id: number) => apiRobo.get(`robosc/comercial/leads/${id}`).then(r => r.data),
    moverEstagio: (id: number, estagio: string) =>
        apiRobo.put(`robosc/comercial/leads/${id}/estagio`, { estagio }).then(r => r.data),
    assumir: (id: number) =>
        apiRobo.post(`robosc/comercial/leads/${id}/assumir`, {}).then(r => r.data),
    transferir: (id: number, body: Dict) =>
        apiRobo.post(`robosc/comercial/leads/${id}/transferir`, body).then(r => r.data),
    liberar: (id: number) =>
        apiRobo.post(`robosc/comercial/leads/${id}/liberar`, {}).then(r => r.data),
    autoResposta: (id: number, ativo: boolean) =>
        apiRobo.put(`robosc/comercial/leads/${id}/auto-resposta`, { ativo }).then(r => r.data),
    removerLead: (id: number) => apiRobo.delete(`robosc/comercial/leads/${id}`).then(r => r.data),
    enriquecerPlaces: (id: number) =>
        apiRobo.post(`robosc/comercial/leads/${id}/places`, {}).then(r => r.data),
    promoverGestor: (id: number) =>
        apiRobo.post(`robosc/comercial/leads/${id}/promover-gestor`, {}).then(r => r.data),
    salvarGestor: (leadId: number, body: Dict) =>
        apiRobo.put(`robosc/comercial/gestores/${leadId}`, body).then(r => r.data),
    loteMover: (body: Dict) => apiRobo.post("robosc/comercial/leads/lote/mover", body).then(r => r.data),
    loteExcluir: (body: Dict) =>
        apiRobo.post("robosc/comercial/leads/lote/excluir", body).then(r => r.data),
    criarLote: (body: Dict) => apiRobo.post("robosc/comercial/leads/lote", body).then(r => r.data),
    listarLotes: (params?: Dict) => apiRobo.get("robosc/comercial/lotes", { params }).then(r => r.data),
    statusLote: (id: number) => apiRobo.get(`robosc/comercial/lotes/${id}`).then(r => r.data),
    cancelarLote: (id: number) =>
        apiRobo.post(`robosc/comercial/lotes/${id}/cancelar`, {}).then(r => r.data),
    importar: (body: Dict) => apiRobo.post("robosc/comercial/leads/importar", body).then(r => r.data),
    listarGestores: () => apiRobo.get("robosc/comercial/gestores").then(r => r.data),
    listarAgenda: (params?: Dict) =>
        apiRobo.get("robosc/comercial/agenda", { params }).then(r => r.data),
    salvarAgenda: (body: Dict) => apiRobo.post("robosc/comercial/agenda", body).then(r => r.data),
    removerAgenda: (id: number) => apiRobo.delete(`robosc/comercial/agenda/${id}`).then(r => r.data),
}

// ─── Convites ─────────────────────────────────────────────────────────────────
export const RoboConvites = {
    listar: (params?: Dict) => apiRobo.get("robosc/convites", { params }).then(r => r.data),
    disparar: (leadId: number, body?: Dict) =>
        apiRobo.post(`robosc/convites/${leadId}/disparar`, body || {}).then(r => r.data),
    migrar: (leadId: number) =>
        apiRobo.post(`robosc/convites/${leadId}/migrar`, {}).then(r => r.data),
    pausar: (leadId: number) =>
        apiRobo.post(`robosc/convites/${leadId}/pausar`, {}).then(r => r.data),
    retomar: (leadId: number) =>
        apiRobo.post(`robosc/convites/${leadId}/retomar`, {}).then(r => r.data),
    remover: (leadId: number) => apiRobo.delete(`robosc/convites/${leadId}`).then(r => r.data),
}

// ─── Enquetes ─────────────────────────────────────────────────────────────────
export const RoboEnquetes = {
    listar: () => apiRobo.get("robosc/enquetes").then(r => r.data),
    criar: (body: Dict) => apiRobo.post("robosc/enquetes", body).then(r => r.data),
    detalhe: (id: number) => apiRobo.get(`robosc/enquetes/${id}`).then(r => r.data),
    atualizarAutoRespostas: (id: number, autoRespostas: Dict[]) =>
        apiRobo.put(`robosc/enquetes/${id}/auto-respostas`, { autoRespostas }).then(r => r.data),
    enviar: (id: number, chatIds: number[]) =>
        apiRobo.post(`robosc/enquetes/${id}/enviar`, { chatIds }).then(r => r.data),
    remover: (id: number) => apiRobo.delete(`robosc/enquetes/${id}`).then(r => r.data),
}

// ─── Comunidade ────────────────────────────────────────────────────────────────
export const RoboComunidade = {
    listarAlertas: (params?: Dict) =>
        apiRobo.get("robosc/comunidade/alertas", { params }).then(r => r.data),
    marcarLido: (id: number) =>
        apiRobo.post(`robosc/comunidade/alertas/${id}/lido`, {}).then(r => r.data),
    marcarTodosLidos: (body?: Dict) =>
        apiRobo.post("robosc/comunidade/alertas/lidos", body || {}).then(r => r.data),
    removerAlerta: (id: number) =>
        apiRobo.delete(`robosc/comunidade/alertas/${id}`).then(r => r.data),
    listarSpammers: () => apiRobo.get("robosc/comunidade/spammers").then(r => r.data),
    removerSpammer: (id: number) =>
        apiRobo.delete(`robosc/comunidade/spammers/${id}`).then(r => r.data),
    listarFornecedores: (busca?: string) =>
        apiRobo.get("robosc/comunidade/fornecedores", { params: busca ? { busca } : undefined }).then(r => r.data),
    removerFornecedor: (id: number) =>
        apiRobo.delete(`robosc/comunidade/fornecedores/${id}`).then(r => r.data),
    listarContatos: (busca?: string) =>
        apiRobo.get("robosc/comunidade/contatos", { params: busca ? { busca } : undefined }).then(r => r.data),
    removerContato: (id: number) =>
        apiRobo.delete(`robosc/comunidade/contatos/${id}`).then(r => r.data),
    listarAllowlist: () => apiRobo.get("robosc/comunidade/allowlist").then(r => r.data),
    adicionarAllowlist: (body: Dict) =>
        apiRobo.post("robosc/comunidade/allowlist", body).then(r => r.data),
    removerAllowlist: (id: number) =>
        apiRobo.delete(`robosc/comunidade/allowlist/${id}`).then(r => r.data),
    listarOptOut: () => apiRobo.get("robosc/comunidade/opt-out").then(r => r.data),
    adicionarOptOut: (body: Dict) =>
        apiRobo.post("robosc/comunidade/opt-out", body).then(r => r.data),
    removerOptOut: (id: number) =>
        apiRobo.delete(`robosc/comunidade/opt-out/${id}`).then(r => r.data),
    listarParticipantes: (params?: Dict) =>
        apiRobo.get("robosc/comunidade/participantes", { params }).then(r => r.data),
}

// ─── Cidades ──────────────────────────────────────────────────────────────────
export const RoboCidades = {
    listar: () => apiRobo.get("robosc/cidades").then(r => r.data),
    panorama: () => apiRobo.get("robosc/cidades/panorama").then(r => r.data),
    associarGrupo: (body: Dict) => apiRobo.post("robosc/cidades/grupo", body).then(r => r.data),
    desassociarGrupo: (params: Dict) =>
        apiRobo.delete("robosc/cidades/grupo", { params }).then(r => r.data),
    setGestor: (body: Dict) => apiRobo.put("robosc/cidades/gestor", body).then(r => r.data),
}

export default apiRobo
